 
import React, { useMemo } from 'react';
import {
  Box,
  dateValidatorByFormat,
  Field,
  FieldTypes,
  Inscription,
} from '@common-fe/common-fe';
import dayjs from 'dayjs';
import * as yup from 'yup';

import {
  DATE_INVALID_TEXT,
  DATE_ONLY_FUTURE_TEXT,
  DEFAULT_DATE_FORMAT,
  FieldStateKey,
  INTEGER_VALUE_TEXT,
  INVALID_RANGE_TEXT,
  PermissionKey,
  REQUIRED_TEXT,
} from '@/common/constants';
import { useHideFields } from '@/modules/employer/components/SetupEmployer/hooks';
import { useSetupEmployerMode } from '@/modules/employer/components/SetupEmployer/hooks/useSetupEmployerMode';
import { EmployerDto } from '@/modules/employer/types';
import useGetPermissionToEditOverridableField from '@/modules/user/hooks/useGetPermissionToEditOverridableField';
import { isDateInFuture } from '@/utils';

  const currencyValueValidate = (minValue = 1, maxValue = 999999) => yup
    .string()
    .test({
      test: (val) => {
        if (val) {
          const value = Number(val);
          return value >= minValue && value <= maxValue;
        }
        return true;
      },
      message: INVALID_RANGE_TEXT(minValue, maxValue),
    })
    .test({
      test: (val) => {
        if (val) {
          const value = Number(val);
          return Number.isInteger(value);
        }
        return true;
      },
      message: INTEGER_VALUE_TEXT,
    });

  const percentValueValidate = yup
    .string()
    .test({
      test: (val) => {
        if (val) {
          const value = Number(val);
          return value >= 1 && value <= 100;
        }
        return true;
      },
      message: INVALID_RANGE_TEXT(1, 100),
    });

const usePreFundFields = (
  formattedData: EmployerDto,
  activateMode?: boolean,
  isLoading?: boolean,
  isAllowPrefund?: boolean,
) => {
  const replenishmentCreepMinPermProps = useGetPermissionToEditOverridableField(
    PermissionKey.PREFUND_REPLENISHMENT_CREEP_MIN,
    FieldStateKey.PREFUND_REPLENISHMENT_CREEP_MIN,
  );
  const replenishmentCreepPercentPermProps = useGetPermissionToEditOverridableField(
    PermissionKey.PREFUND_REPLENISHMENT_CREEP_PERCENT,
    FieldStateKey.PREFUND_REPLENISHMENT_CREEP_PERCENT,
  );
  const replenishmentMinValuePermProps = useGetPermissionToEditOverridableField(
    PermissionKey.PREFUND_REPLENISHMENT_MIN_VALUE,
    FieldStateKey.PREFUND_REPLENISHMENT_MIN_VALUE,
  );
  const replenishmentPercentPermProps = useGetPermissionToEditOverridableField(
    PermissionKey.PREFUND_REPLENISHMENT_PERCENT,
    FieldStateKey.PREFUND_REPLENISHMENT_PERCENT,
  );
  const allowRefund = useGetPermissionToEditOverridableField(
    PermissionKey.ALLOW_PREFUND,
    FieldStateKey.ALLOW_PREFUND,
  );
  const { viewMode } = useSetupEmployerMode();

  const fields =  useMemo(() => {
    if (isLoading) return [];

    return [
      {
        name: 'allowPrefund',
        type: FieldTypes.Checkbox,
        disabled: viewMode,
        label: (
          <Box flex={{ grow: 1 }}>
            <Box direction="row" height={{ max: '20px' }} margin={{ bottom: '4px' }}>
              <Inscription weight="bold" color="textBody" lineHeight="20px">
                Enable Pre-Fund
              </Inscription>
            </Box>
            <Box>
              <Inscription weight={500} color="textSecondary" size="small" lineHeight="18px">
                Enabling allows for either employer or plan level pre-funding
              </Inscription>
            </Box>
          </Box>
        ),
        value: formattedData?.overridableProperties?.allowPrefund || '',
        ...allowRefund,
      },
      ...(isAllowPrefund ? [
        {
          name: 'migrationPreFundStartDate',
          type: FieldTypes.Date,
          disabled: viewMode,
          placeholder: 'Select date',
          controlStyles: { width: '180px' },
          inputWrapCssClass: 'small-input',
          validator: yup.string()
            .test({
              test: (val) => val
                ? isDateInFuture(val) || val === formattedData?.overridableProperties?.prefundInitialDelayDate
                : true,
              message: DATE_ONLY_FUTURE_TEXT,
            })
            .test({
              test: (val) => val ? dateValidatorByFormat(val, DEFAULT_DATE_FORMAT) : true,
              message: DATE_INVALID_TEXT, 
            }),
          minDate: dayjs(),
          label: (
            <Box flex={{ grow: 1 }}>
              <Box direction="row" height={{ max: '20px' }} margin={{ bottom: '4px' }}>
                <Inscription weight="bold" color="textBody" lineHeight="20px">
                  Migration Completion Date
                </Inscription>
              </Box>
              <Box>
                <Inscription weight={500} color="textSecondary" size="small" lineHeight="18px">
                  Date all migration enrollments will be loaded into the system. Pre-fund will be calculated on or after the completion date for currently active plans. Future plan years will calculate pre-fund 15 days prior to the plan year start.
                </Inscription>
              </Box>
            </Box>
          ),
          value: formattedData?.overridableProperties?.prefundInitialDelayDate || '',
        },
        {
          name: 'preFundPercent',
          type: FieldTypes.Number,
          disabled: viewMode,
          validator: activateMode
            ? percentValueValidate.required(REQUIRED_TEXT)
            : percentValueValidate,
          placeholder: 'Select Percent',
          label: (
            <Box flex={{ grow: 1 }}>
              <Box direction="row" height={{ max: '20px' }} margin={{ bottom: '4px' }}>
                <Inscription
                  color="danger"
                  margin={{ right: '3px', top: '-3px' }}
                >
                  *
                </Inscription>
                <Inscription weight="bold" color="textBody" lineHeight="20px">
                  Percent
                </Inscription>
              </Box>
              <Box>
                <Inscription weight={500} color="textSecondary" size="small" lineHeight="18px">
                  Date initial pre-fund will be calculated after migration
                </Inscription>
              </Box>
            </Box>
          ),
          value: formattedData?.overridableProperties?.replenishmentPercent || 4,
          ...replenishmentPercentPermProps,
        },
        {
          name: 'preFundMinimumValue',
          type: FieldTypes.Currency,
          disabled: viewMode,
          validator: activateMode
            ? currencyValueValidate(0).required(REQUIRED_TEXT)
            : currencyValueValidate(0),
          placeholder: 'Select Minimum Val...',
          label: 'Minimum value',
          showRequireIcon: true,
          value: `${formattedData?.overridableProperties?.replenishmentMinValue ?? 500}`,
          ...replenishmentMinValuePermProps,
        },
        {
          name: 'preFundElectionChangePercentage',
          type: FieldTypes.Number,
          disabled: viewMode,
          validator: activateMode
            ? percentValueValidate.required(REQUIRED_TEXT)
            : percentValueValidate,
          placeholder: 'Select Election Cha...',
          label: (
            <Box flex={{ grow: 1 }}>
              <Box direction="row" height={{ max: '20px' }} margin={{ bottom: '4px' }}>
                <Inscription
                  color="danger"
                  margin={{ right: '3px', top: '-3px' }}
                >
                  *
                </Inscription>
                <Inscription weight="bold" color="textBody" lineHeight="20px">
                  Election change percentage
                </Inscription>
              </Box>
              <Box>
                <Inscription weight={500} color="textSecondary" size="small" lineHeight="18px">
                  Increased elections from the previous pre-fund where additional funds will be collected
                </Inscription>
              </Box>
            </Box>
          ),
          value: formattedData?.overridableProperties?.replenishmentCreepPercent || 25,
          ...replenishmentCreepPercentPermProps,
        },
        {
          name: 'preFundMinimumThreshold',
          type: FieldTypes.Currency,
          disabled: viewMode,
          validator: activateMode
            ? currencyValueValidate(0).required(REQUIRED_TEXT)
            : currencyValueValidate(0),
          placeholder: 'Select Minimum Thr...',
          label: 'Minimum threshold',
          showRequireIcon: true,
          value: `${formattedData?.overridableProperties?.replenishmentCreepMin ?? 100}`,
          hasHorizontalLine: true,
          ...replenishmentCreepMinPermProps,
        },] : []),
    ] as Field[];
  }, [
    isLoading,
    viewMode,
    formattedData,
    activateMode,
    replenishmentCreepMinPermProps,
    replenishmentCreepPercentPermProps,
    replenishmentMinValuePermProps,
    replenishmentPercentPermProps,
    allowRefund,
    isAllowPrefund,
  ]);

  const formattedFields = useHideFields(fields);
  return formattedFields;
};

export default usePreFundFields;
